import React, { useContext, useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import api from '../services/api';

function MetricCard({ label, value, sub, accent }) {
  return (
    <div
      style={{
        background: '#1a1a2e',
        border: `1px solid ${accent || '#2a2a4a'}`,
        borderRadius: 12,
        padding: '24px 28px',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
      }}
    >
      <div style={{ color: '#8888aa', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
        {label}
      </div>
      <div style={{ color: '#ffffff', fontSize: 32, fontWeight: 700, lineHeight: 1.1 }}>
        {value}
      </div>
      {sub && (
        <div style={{ color: '#6666aa', fontSize: 13, marginTop: 2 }}>{sub}</div>
      )}
    </div>
  );
}

function TierBadge({ tier }) {
  const colors = {
    free: { bg: '#1e293b', text: '#94a3b8', border: '#334155' },
    pro: { bg: '#1e3a5f', text: '#60a5fa', border: '#2563eb' },
    business: { bg: '#1a2e1a', text: '#4ade80', border: '#16a34a' },
    enterprise: { bg: '#2e1a2e', text: '#c084fc', border: '#9333ea' },
  };
  const c = colors[tier] || colors.free;
  return (
    <span style={{
      background: c.bg,
      color: c.text,
      border: `1px solid ${c.border}`,
      borderRadius: 6,
      padding: '2px 10px',
      fontSize: 12,
      fontWeight: 600,
      textTransform: 'capitalize',
    }}>
      {tier}
    </span>
  );
}

function formatCurrency(cents) {
  if (cents === undefined || cents === null) return '$0';
  return '$' + (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

function formatMoney(dollars) {
  if (!dollars && dollars !== 0) return '$0';
  return '$' + Number(dollars).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function SuperAdminDashboard() {
  const { logout, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);

  const fetchMetrics = useCallback(async () => {
    try {
      setError(null);
      const res = await api.get('/admin/metrics');
      setMetrics(res.data);
      setLastRefresh(new Date());
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load metrics');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMetrics();
    // Auto-refresh every 60 seconds
    const interval = setInterval(fetchMetrics, 60000);
    return () => clearInterval(interval);
  }, [fetchMetrics]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const containerStyle = {
    minHeight: '100vh',
    background: '#0d0d1a',
    color: '#ffffff',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  };

  const headerStyle = {
    background: '#12122a',
    borderBottom: '1px solid #1e1e3f',
    padding: '16px 32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  };

  const mainStyle = {
    maxWidth: 1280,
    margin: '0 auto',
    padding: '32px 24px',
  };

  const sectionHeaderStyle = {
    color: '#8888aa',
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    marginBottom: 16,
    marginTop: 36,
  };

  const gridStyle = (cols) => ({
    display: 'grid',
    gridTemplateColumns: `repeat(${cols}, 1fr)`,
    gap: 16,
  });

  if (loading) {
    return (
      <div style={{ ...containerStyle, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ color: '#8888aa', fontSize: 18 }}>Loading metrics...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ ...containerStyle, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16 }}>
        <div style={{ color: '#f87171', fontSize: 18 }}>{error}</div>
        <button onClick={fetchMetrics} style={{ background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 24px', cursor: 'pointer', fontSize: 14 }}>
          Retry
        </button>
      </div>
    );
  }

  const m = metrics || {};
  const users = m.users || {};
  const subs = m.subscriptions || {};
  const revenue = m.revenue || {};
  const activity = m.activity || {};
  const recentSignups = m.recentSignups || [];

  return (
    <div style={containerStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{
            background: 'linear-gradient(135deg, #2563eb, #7c3aed)',
            borderRadius: 10,
            width: 36,
            height: 36,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 16,
            fontWeight: 800,
            color: '#fff',
          }}>
            B
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16, color: '#fff' }}>ONLYnumber Admin</div>
            <div style={{ fontSize: 12, color: '#6666aa' }}>BFF Software LLC</div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
          {lastRefresh && (
            <div style={{ fontSize: 12, color: '#555577' }}>
              Updated {lastRefresh.toLocaleTimeString()}
            </div>
          )}
          <button
            onClick={fetchMetrics}
            style={{
              background: '#1a1a3a',
              color: '#8888cc',
              border: '1px solid #2a2a4a',
              borderRadius: 8,
              padding: '8px 16px',
              cursor: 'pointer',
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            Refresh
          </button>
          <div style={{ fontSize: 13, color: '#555577' }}>{user?.email}</div>
          <button
            onClick={handleLogout}
            style={{
              background: 'transparent',
              color: '#f87171',
              border: '1px solid #7f1d1d',
              borderRadius: 8,
              padding: '8px 16px',
              cursor: 'pointer',
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            Sign Out
          </button>
        </div>
      </div>

      <div style={mainStyle}>
        {/* Revenue */}
        <div style={sectionHeaderStyle}>Revenue</div>
        <div style={gridStyle(3)}>
          <MetricCard
            label="Monthly Recurring Revenue"
            value={formatMoney(revenue.mrr)}
            sub={`${subs.totalPaying || 0} paying subscribers`}
            accent="#2563eb"
          />
          <MetricCard
            label="Annual Recurring Revenue"
            value={formatMoney(revenue.arr)}
            sub="MRR x 12"
            accent="#7c3aed"
          />
          <MetricCard
            label="Active Subscribers"
            value={subs.totalPaying || 0}
            sub={`Pro: ${subs.pro || 0} @ $${revenue.pricePerPro || 17}/mo   Business: ${subs.business || 0} @ $${revenue.pricePerBusiness || 27}/mo`}
            accent="#16a34a"
          />
        </div>

        {/* Users */}
        <div style={sectionHeaderStyle}>Users</div>
        <div style={gridStyle(4)}>
          <MetricCard
            label="Total Users"
            value={(users.total || 0).toLocaleString()}
            accent="#2a2a5a"
          />
          <MetricCard
            label="New Today"
            value={users.newToday || 0}
            accent="#2a2a5a"
          />
          <MetricCard
            label="New This Week"
            value={users.newThisWeek || 0}
            accent="#2a2a5a"
          />
          <MetricCard
            label="New This Month"
            value={users.newThisMonth || 0}
            accent="#2a2a5a"
          />
        </div>

        {/* Plan Breakdown */}
        <div style={sectionHeaderStyle}>Plan Breakdown</div>
        <div style={gridStyle(3)}>
          <div style={{
            background: '#1a1a2e',
            border: '1px solid #2a2a4a',
            borderRadius: 12,
            padding: '24px 28px',
          }}>
            <div style={{ color: '#8888aa', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 16 }}>
              Starter (Free)
            </div>
            <div style={{ fontSize: 40, fontWeight: 700, color: '#94a3b8' }}>{subs.free || 0}</div>
            <div style={{ marginTop: 12, background: '#0d0d1a', borderRadius: 6, height: 6, overflow: 'hidden' }}>
              <div style={{
                width: `${users.total ? Math.round(((subs.free || 0) / users.total) * 100) : 0}%`,
                height: '100%',
                background: '#475569',
                borderRadius: 6,
              }} />
            </div>
            <div style={{ color: '#555577', fontSize: 12, marginTop: 6 }}>
              {users.total ? Math.round(((subs.free || 0) / users.total) * 100) : 0}% of users
            </div>
          </div>
          <div style={{
            background: '#1a1a2e',
            border: '1px solid #1d4ed8',
            borderRadius: 12,
            padding: '24px 28px',
          }}>
            <div style={{ color: '#60a5fa', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 16 }}>
              Pro
            </div>
            <div style={{ fontSize: 40, fontWeight: 700, color: '#60a5fa' }}>{subs.pro || 0}</div>
            <div style={{ marginTop: 12, background: '#0d0d1a', borderRadius: 6, height: 6, overflow: 'hidden' }}>
              <div style={{
                width: `${users.total ? Math.round(((subs.pro || 0) / users.total) * 100) : 0}%`,
                height: '100%',
                background: '#2563eb',
                borderRadius: 6,
              }} />
            </div>
            <div style={{ color: '#555577', fontSize: 12, marginTop: 6 }}>
              {users.total ? Math.round(((subs.pro || 0) / users.total) * 100) : 0}% of users &bull; ${revenue.pricePerPro || 17}/mo each
            </div>
          </div>
          <div style={{
            background: '#1a1a2e',
            border: '1px solid #15803d',
            borderRadius: 12,
            padding: '24px 28px',
          }}>
            <div style={{ color: '#4ade80', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 16 }}>
              Business
            </div>
            <div style={{ fontSize: 40, fontWeight: 700, color: '#4ade80' }}>{subs.business || 0}</div>
            <div style={{ marginTop: 12, background: '#0d0d1a', borderRadius: 6, height: 6, overflow: 'hidden' }}>
              <div style={{
                width: `${users.total ? Math.round(((subs.business || 0) / users.total) * 100) : 0}%`,
                height: '100%',
                background: '#16a34a',
                borderRadius: 6,
              }} />
            </div>
            <div style={{ color: '#555577', fontSize: 12, marginTop: 6 }}>
              {users.total ? Math.round(((subs.business || 0) / users.total) * 100) : 0}% of users &bull; ${revenue.pricePerBusiness || 27}/mo each
            </div>
          </div>
        </div>

        {/* Activity */}
        <div style={sectionHeaderStyle}>Platform Activity</div>
        <div style={gridStyle(4)}>
          <MetricCard
            label="Total Calls"
            value={(activity.totalCalls || 0).toLocaleString()}
            sub={`${(activity.callsThisMonth || 0).toLocaleString()} this month`}
            accent="#2a2a4a"
          />
          <MetricCard
            label="Calls This Month"
            value={(activity.callsThisMonth || 0).toLocaleString()}
            accent="#2a2a4a"
          />
          <MetricCard
            label="Total Voicemails"
            value={(activity.totalVoicemails || 0).toLocaleString()}
            accent="#2a2a4a"
          />
          <MetricCard
            label="Video Rooms"
            value={(activity.totalVideoRooms || 0).toLocaleString()}
            sub={`${(activity.videoRoomsThisMonth || 0).toLocaleString()} this month`}
            accent="#2a2a4a"
          />
        </div>

        {/* Recent Signups */}
        <div style={sectionHeaderStyle}>Recent Signups</div>
        <div style={{
          background: '#1a1a2e',
          border: '1px solid #2a2a4a',
          borderRadius: 12,
          overflow: 'hidden',
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #2a2a4a' }}>
                <th style={{ padding: '14px 20px', textAlign: 'left', color: '#6666aa', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Email</th>
                <th style={{ padding: '14px 20px', textAlign: 'left', color: '#6666aa', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Plan</th>
                <th style={{ padding: '14px 20px', textAlign: 'left', color: '#6666aa', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Joined</th>
              </tr>
            </thead>
            <tbody>
              {recentSignups.length === 0 ? (
                <tr>
                  <td colSpan={3} style={{ padding: '24px 20px', textAlign: 'center', color: '#555577', fontSize: 14 }}>
                    No signups yet
                  </td>
                </tr>
              ) : (
                recentSignups.map((signup, i) => (
                  <tr
                    key={i}
                    style={{
                      borderBottom: i < recentSignups.length - 1 ? '1px solid #1e1e3a' : 'none',
                      transition: 'background 0.15s',
                    }}
                  >
                    <td style={{ padding: '14px 20px', color: '#ccccee', fontSize: 14 }}>{signup.email}</td>
                    <td style={{ padding: '14px 20px' }}>
                      <TierBadge tier={signup.tier || 'free'} />
                    </td>
                    <td style={{ padding: '14px 20px', color: '#666688', fontSize: 14 }}>{formatDate(signup.createdAt)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div style={{ marginTop: 48, textAlign: 'center', color: '#333355', fontSize: 12 }}>
          BFF Software LLC &bull; ONLYnumber.online &bull; Super Admin
        </div>
      </div>
    </div>
  );
}
